// src/components/proposal-formats/FormatCalcLinesSection.tsx

import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  deleteFormatCalcLine,
  reorderFormatCalcLines,
  upsertFormatCalcLine,
} from "@/lib/opportunity/formatCalcLineClient";
import {
  FORMAT_CALC_PLACEMENT_LABELS,
  type FormatCalcLine,
  type FormatCalcPlacement,
} from "@/lib/opportunity/formatCalcLineTypes";
import { evaluatePlacementLines } from "@/lib/opportunity/formatCalcEval";
import { FormatCalcLineRow } from "./FormatCalcLineRow";

export type FormatCalcLinesSectionProps = {
  placement: FormatCalcPlacement;
  lines: FormatCalcLine[];
  formatKey: OpportunityFormatKey;
  productType: string;
  onChanged: () => void;
  embedded?: boolean;
  /** Running total before the first line in this zone (preview/editor). */
  percentBasis?: number;
};

export function FormatCalcLinesSection(props: FormatCalcLinesSectionProps) {
  const { placement, lines, formatKey, productType, onChanged, embedded, percentBasis = 0 } = props;
  const zoneLines = lines
    .filter((l) => l.placement === placement)
    .sort((a, b) => a.sort_order - b.sort_order);

  const evaluated = evaluatePlacementLines(zoneLines, undefined, percentBasis).evaluated;
  const amountById = new Map(evaluated.map((e) => [e.id, e.amount]));

  async function addLine() {
    const nextOrder = zoneLines.length;
    const r = await upsertFormatCalcLine({
      format_key: formatKey,
      product_type: productType,
      placement,
      sort_order: nextOrder,
      label: "New line",
      amount_mode: "fixed",
      default_amount: 0,
      visible_on_proposal: true,
      commissionable: false,
      include_in_deposit: true,
    });
    if (r.ok) onChanged();
  }

  async function moveLine(id: string, direction: "up" | "down") {
    const idx = zoneLines.findIndex((l) => l.id === id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= zoneLines.length) return;
    const reordered = [...zoneLines];
    const tmp = reordered[idx];
    reordered[idx] = reordered[swapIdx];
    reordered[swapIdx] = tmp;
    const r = await reorderFormatCalcLines({
      format_key: formatKey,
      product_type: productType,
      placement,
      ordered_ids: reordered.map((l) => l.id),
    });
    if (r.ok) onChanged();
  }

  return (
    <section className={embedded ? "space-y-2" : "space-y-2"}>
      <div className="flex items-center justify-between gap-2">
        {embedded ? null : (
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            {FORMAT_CALC_PLACEMENT_LABELS[placement]}
          </h4>
        )}
        <button
          type="button"
          onClick={() => void addLine()}
          className="px-2 py-1 rounded-lg border border-gray-300 text-[11px] hover:bg-gray-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
        >
          + Add line
        </button>
      </div>
      <p className="text-[11px] text-gray-500 dark:text-zinc-400">
        {placement === "before_subtotal"
          ? "% lines calculate on the base amount plus all prior lines above them in this group."
          : "% lines calculate on the adjusted subtotal (base + before-subtotal lines) plus prior lines in this group."}
      </p>
      {zoneLines.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400 italic">No lines yet.</p>
      ) : (
        zoneLines.map((line, idx) => (
          <FormatCalcLineRow
            key={line.id}
            line={line}
            evaluatedAmount={amountById.get(line.id) ?? null}
            canMoveUp={idx > 0}
            canMoveDown={idx < zoneLines.length - 1}
            onSave={async (payload) => {
              const r = await upsertFormatCalcLine(payload);
              if (r.ok) onChanged();
              return r.ok ? { ok: true } : { ok: false, error: r.error };
            }}
            onDelete={async (id) => {
              const r = await deleteFormatCalcLine(id);
              if (r.ok) onChanged();
              return r.ok ? { ok: true } : { ok: false, error: r.error };
            }}
            onMove={(dir) => void moveLine(line.id, dir)}
          />
        ))
      )}
    </section>
  );
}
